import { authApi } from '@/services/authApi'

const state = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: false,
  error: null,
}

const getters = {
  user: (state) => state.user,
  isLoggedIn: (state) => state.isAuthenticated && state.accessToken,
  userRole: (state) => state.user?.role || null,
  userName: (state) => state.user?.name || '',
  userEmail: (state) => state.user?.email || '',
  userIsActive: (state) => state.user?.is_active || false,
  userCreatedAt: (state) => state.user?.created_at || null,
  userUpdatedAt: (state) => state.user?.updated_at || null,
  userGroup: (state) => state.user?.group || null,
  userGroupId: (state) => state.user?.group_id || null,
  userGroups: (state) => state.user?.groups || [],
  isAdmin: (state) => state.user?.role === 'admin',
  isTeacher: (state) => state.user?.role === 'teacher',
  isStudent: (state) => state.user?.role === 'student',
  loading: (state) => state.loading,
  error: (state) => state.error,
  getDashboardRoute: (state) => {
    if (!state.user) return '/login'

    // Redirect students to pin-access instead of dashboard
    if (state.user.role === 'student') {
      return '/pin-access'
    }

    return '/dashboard'
  },
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  CLEAR_ERROR(state) {
    state.error = null
  },

  SET_AUTH_DATA(state, { user, access_token, refresh_token }) {
    state.user = user
    state.accessToken = access_token
    state.refreshToken = refresh_token
    state.isAuthenticated = true

    // Store in localStorage
    localStorage.setItem('access_token', access_token)
    localStorage.setItem('refresh_token', refresh_token)
    localStorage.setItem('user', JSON.stringify(user))
  },

  SET_USER(state, user) {
    state.user = user
    localStorage.setItem('user', JSON.stringify(user))
  },

  SET_TOKENS(state, { access_token, refresh_token }) {
    state.accessToken = access_token
    state.refreshToken = refresh_token

    localStorage.setItem('access_token', access_token)
    localStorage.setItem('refresh_token', refresh_token)
  },

  CLEAR_AUTH_DATA(state) {
    state.user = null
    state.accessToken = null
    state.refreshToken = null
    state.isAuthenticated = false
    state.error = null

    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
  },

  INITIALIZE_AUTH(state) {
    const token = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')
    const userStr = localStorage.getItem('user')

    if (token && userStr) {
      try {
        state.accessToken = token
        state.refreshToken = refreshToken
        state.user = JSON.parse(userStr)
        state.isAuthenticated = true
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error)
        // Clear invalid data
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
      }
    }
  },
}

const actions = {
  async login({ commit }, credentials) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await authApi.login(credentials)

      // La respuesta ya viene procesada por el interceptor de Axios
      // Puede venir como { success: true, data: {...} } o directamente como { user, access_token, refresh_token }
      let user, access_token, refresh_token

      if (response.success && response.data) {
        ;({ user, access_token, refresh_token } = response.data)
      } else if (response.user && response.access_token) {
        ;({ user, access_token, refresh_token } = response)
      } else {
        throw new Error('Invalid response format')
      }

      commit('SET_AUTH_DATA', { user, access_token, refresh_token })
      return { success: true, user }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async register({ commit }, userData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await authApi.register(userData)

      // La respuesta ya viene procesada por el interceptor de Axios
      let user, access_token, refresh_token

      if (response.success && response.data) {
        ;({ user, access_token, refresh_token } = response.data)
      } else if (response.user && response.access_token) {
        ;({ user, access_token, refresh_token } = response)
      } else {
        throw new Error('Invalid response format')
      }

      commit('SET_AUTH_DATA', { user, access_token, refresh_token })
      return { success: true, user }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchProfile({ commit, state }) {
    if (!state.accessToken) return { success: false, error: 'No access token' }

    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await authApi.getCurrentUser()

      // La respuesta ya viene procesada por el interceptor de Axios
      let user

      if (response.success && response.data) {
        user = response.data.user
      } else if (response.user) {
        user = response.user
      } else {
        user = response // La respuesta completa puede ser el usuario
      }

      commit('SET_USER', user)
      return { success: true, user }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to fetch profile'
      commit('SET_ERROR', errorMessage)

      if (error.status === 401) {
        commit('CLEAR_AUTH_DATA')
      }

      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async refreshAccessToken({ commit, state }) {
    if (!state.refreshToken) {
      commit('CLEAR_AUTH_DATA')
      return false
    }

    try {
      const response = await authApi.refreshToken()

      if (response.success) {
        const { access_token, refresh_token } = response.data
        commit('SET_TOKENS', { access_token, refresh_token })
        return true
      }

      return false
    } catch (error) {
      commit('CLEAR_AUTH_DATA')
      return false
    }
  },

  async logout({ commit, state }) {
    try {
      if (state.accessToken) {
        await authApi.logout()
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      commit('CLEAR_AUTH_DATA')
    }
  },

  initializeAuth({ commit }) {
    commit('INITIALIZE_AUTH')
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
